import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { usePacientes } from '../../hooks/usePacientes';
import {
  createInvoice,
  getFinancialSettings,
  saveFinancialSettings,
  NotaFiscal,
} from '../../services/financialService';
import { generateInvoicePDF } from '../../utils/pdfGenerator';
import { Consulta } from '../../services/mockData';
import { buscarCEP, formatarCEP } from '../../services/cepService';
import './Financial.css';

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  consulta?: Consulta | null;
  onSuccess?: () => void;
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({
  isOpen,
  onClose,
  consulta,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { pacientes } = usePacientes();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [buscandoCEP, setBuscandoCEP] = useState(false);

  const [formData, setFormData] = useState({
    pacienteId: '',
    valor: '',
    descricaoServico: 'Consulta nutricional',
    dataVencimento: '',
    observacoes: '',
    assinaturaNome: '',
    assinaturaData: '',
    emitente: {
      nome: '',
      cpf: '',
      cnpj: '',
      endereco: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: '',
      telefone: '',
      email: '',
      inscricaoEstadual: '',
      crn: '',
    },
  });

  useEffect(() => {
    if (isOpen && user?.uid) {
      loadSettings();
      if (consulta) {
        setFormData((prev) => ({
          ...prev,
          pacienteId: consulta.pacienteId,
          valor: consulta.valor ? consulta.valor.toFixed(2).replace('.', ',') : '',
          descricaoServico: `Consulta nutricional - ${consulta.tipo === 'primeira' ? 'Primeira consulta' : 'Retorno'}`,
        }));
      }
    }
  }, [isOpen, user?.uid, consulta]);

  const loadSettings = async () => {
    if (!user?.uid) return;

    try {
      const settings = await getFinancialSettings(user.uid);
      if (settings) {
        setFormData((prev) => ({
          ...prev,
          emitente: {
            nome: settings.emitente.nome || prev.emitente.nome,
            cpf: settings.emitente.cpf || prev.emitente.cpf,
            cnpj: settings.emitente.cnpj || prev.emitente.cnpj,
            endereco: settings.emitente.endereco || prev.emitente.endereco,
            bairro: settings.emitente.bairro || prev.emitente.bairro,
            cidade: settings.emitente.cidade || prev.emitente.cidade,
            estado: settings.emitente.estado || prev.emitente.estado,
            cep: settings.emitente.cep || prev.emitente.cep,
            telefone: settings.emitente.telefone || prev.emitente.telefone,
            email: settings.emitente.email || prev.emitente.email,
            inscricaoEstadual: settings.emitente.inscricaoEstadual || prev.emitente.inscricaoEstadual,
            crn: settings.emitente.crn || prev.emitente.crn,
          },
        }));
      } else {
        setShowSettings(true);
      }
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
      setShowSettings(true);
    }
  };

  const handleSaveSettings = async () => {
    if (!user?.uid) return;

    setSavingSettings(true);
    try {
      await saveFinancialSettings(user.uid, formData.emitente);
      setShowSettings(false);
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar configurações');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleBuscarCEP = async () => {
    const cep = formData.emitente.cep;
    if (!cep || cep.replace(/\D/g, '').length !== 8) {
      setError('CEP inválido. Digite um CEP com 8 dígitos.');
      return;
    }

    setBuscandoCEP(true);
    setError(null);

    try {
      const cepData = await buscarCEP(cep);
      
      if (cepData) {
        setFormData((prev) => ({
          ...prev,
          emitente: {
            ...prev.emitente,
            endereco: cepData.logradouro || prev.emitente.endereco,
            bairro: cepData.bairro || prev.emitente.bairro || '',
            cidade: cepData.localidade || prev.emitente.cidade,
            estado: cepData.uf || prev.emitente.estado,
            cep: formatarCEP(cepData.cep),
          },
        }));
      } else {
        setError('CEP não encontrado. Verifique o CEP digitado.');
      }
    } catch (err: any) {
      setError('Erro ao buscar CEP. Tente novamente.');
      console.error('Erro ao buscar CEP:', err);
    } finally {
      setBuscandoCEP(false);
    }
  };

  const handleCEPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const formatted = formatarCEP(value);
    setFormData({
      ...formData,
      emitente: { ...formData.emitente, cep: formatted },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!user?.uid) {
      setError('Usuário não autenticado');
      return;
    }

    if (!formData.pacienteId) {
      setError('Selecione um paciente');
      return;
    }

    if (!formData.valor) {
      setError('Informe o valor da consulta');
      return;
    }

    if (!formData.emitente.nome) {
      setError('Configure os dados do emitente');
      setShowSettings(true);
      return;
    }

    const paciente = pacientes.find((p) => p.id === formData.pacienteId);
    if (!paciente) {
      setError('Paciente não encontrado');
      return;
    }

    // Validar que o emitente não é o mesmo que o cliente
    if (formData.emitente.nome.toLowerCase().trim() === paciente.nome.toLowerCase().trim()) {
      setError('O emitente (nutricionista) não pode ser o mesmo que o cliente. Verifique os dados do emitente.');
      setShowSettings(true);
      return;
    }

    setLoading(true);
    try {
      const valor = parseFloat(formData.valor.replace(',', '.'));

      // Garantir que a assinatura use o nome do nutricionista (emitente) se não fornecida
      const nomeAssinatura = formData.assinaturaNome?.trim() || formData.emitente.nome;
      const dataAssinatura = formData.assinaturaData || new Date().toISOString().split('T')[0];

      const invoiceData: Omit<NotaFiscal, 'id' | 'createdAt' | 'updatedAt'> = {
        consultaId: consulta?.id || '',
        pacienteId: formData.pacienteId,
        pacienteNome: paciente.nome,
        valor,
        dataEmissao: new Date().toISOString(),
        dataVencimento: formData.dataVencimento
          ? new Date(formData.dataVencimento).toISOString()
          : null,
        status: 'emitida',
        descricaoServico: formData.descricaoServico,
        // Emitente sempre é o nutricionista (dados das configurações)
        emitente: formData.emitente,
        // Cliente sempre é o paciente
        cliente: {
          nome: paciente.nome,
          email: paciente.email || null,
        },
        observacoes: formData.observacoes?.trim() || null,
        // Assinatura sempre é do nutricionista
        assinatura: {
          nome: nomeAssinatura,
          data: dataAssinatura,
        },
      };

      const invoiceId = await createInvoice(user.uid, invoiceData);

      // Gerar PDF
      const invoice: NotaFiscal = {
        ...invoiceData,
        id: invoiceId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const pdf = generateInvoicePDF(invoice);
      pdf.save(`nota-fiscal-${invoiceId}.pdf`);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao emitir nota fiscal');
      console.error('Erro ao emitir nota fiscal:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={showSettings ? 'Configurar Dados do Emitente' : 'Emitir Nota Fiscal'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading || savingSettings}>
            Cancelar
          </Button>
          {showSettings ? (
            <Button onClick={handleSaveSettings} disabled={savingSettings}>
              {savingSettings ? 'Salvando...' : 'Salvar e Continuar'}
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? 'Emitindo...' : 'Emitir Nota Fiscal'}
            </Button>
          )}
        </>
      }
    >
      {error && <div className="ns-form-error">{error}</div>}

      {showSettings ? (
        <form className="ns-form" onSubmit={(e) => e.preventDefault()}>
          <h4 style={{ marginBottom: '16px' }}>Dados do Emitente (Nutricionista)</h4>

          <label className="ns-field">
            <span>Nome Completo *</span>
            <input
              className="ns-input"
              value={formData.emitente.nome}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  emitente: { ...formData.emitente, nome: e.target.value },
                })
              }
              required
            />
          </label>

          <div className="ns-form-row">
            <label className="ns-field">
              <span>CPF</span>
              <input
                className="ns-input"
                value={formData.emitente.cpf}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emitente: { ...formData.emitente, cpf: e.target.value },
                  })
                }
                placeholder="000.000.000-00"
              />
            </label>

            <label className="ns-field">
              <span>CNPJ</span>
              <input
                className="ns-input"
                value={formData.emitente.cnpj}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emitente: { ...formData.emitente, cnpj: e.target.value },
                  })
                }
                placeholder="00.000.000/0000-00"
              />
            </label>
          </div>

          <label className="ns-field">
            <span>Endereço</span>
            <input
              className="ns-input"
              value={formData.emitente.endereco}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  emitente: { ...formData.emitente, endereco: e.target.value },
                })
              }
            />
          </label>

          <label className="ns-field">
            <span>Bairro</span>
            <input
              className="ns-input"
              value={formData.emitente.bairro}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  emitente: { ...formData.emitente, bairro: e.target.value },
                })
              }
            />
          </label>

          <div className="ns-form-row">
            <label className="ns-field">
              <span>Cidade</span>
              <input
                className="ns-input"
                value={formData.emitente.cidade}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emitente: { ...formData.emitente, cidade: e.target.value },
                  })
                }
              />
            </label>

            <label className="ns-field">
              <span>Estado</span>
              <input
                className="ns-input"
                value={formData.emitente.estado}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emitente: { ...formData.emitente, estado: e.target.value },
                  })
                }
                maxLength={2}
                placeholder="SP"
              />
            </label>

            <label className="ns-field">
              <span>CEP</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  className="ns-input"
                  value={formData.emitente.cep}
                  onChange={handleCEPChange}
                  onBlur={(e) => {
                    const cepLimpo = e.target.value.replace(/\D/g, '');
                    if (cepLimpo.length === 8) {
                      handleBuscarCEP();
                    }
                  }}
                  placeholder="00000-000"
                  maxLength={9}
                  style={{ flex: 1 }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleBuscarCEP}
                  disabled={buscandoCEP || !formData.emitente.cep}
                  style={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                >
                  {buscandoCEP ? '...' : 'Buscar'}
                </Button>
              </div>
            </label>
          </div>

          <div className="ns-form-row">
            <label className="ns-field">
              <span>Telefone</span>
              <input
                className="ns-input"
                value={formData.emitente.telefone}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emitente: { ...formData.emitente, telefone: e.target.value },
                  })
                }
                placeholder="(00) 00000-0000"
              />
            </label>

            <label className="ns-field">
              <span>Email</span>
              <input
                className="ns-input"
                type="email"
                value={formData.emitente.email}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emitente: { ...formData.emitente, email: e.target.value },
                  })
                }
              />
            </label>
          </div>

          <label className="ns-field">
            <span>Inscrição Estadual</span>
            <input
              className="ns-input"
              value={formData.emitente.inscricaoEstadual}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  emitente: { ...formData.emitente, inscricaoEstadual: e.target.value },
                })
              }
            />
          </label>

          <label className="ns-field">
            <span>CRN (Conselho Regional de Nutricionistas)</span>
            <input
              className="ns-input"
              value={formData.emitente.crn}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  emitente: { ...formData.emitente, crn: e.target.value },
                })
              }
              placeholder="Ex: CRN-3 12345"
            />
          </label>
        </form>
      ) : (
        <form className="ns-form" onSubmit={handleSubmit}>
          <label className="ns-field">
            <span>Paciente *</span>
            <select
              className="ns-input"
              value={formData.pacienteId}
              onChange={(e) =>
                setFormData({ ...formData, pacienteId: e.target.value })
              }
              disabled={!!consulta}
              required
            >
              <option value="">Selecione um paciente</option>
              {pacientes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </label>

          <label className="ns-field">
            <span>Valor (R$) *</span>
            <input
              className="ns-input"
              type="text"
              value={formData.valor}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d,.-]/g, '');
                setFormData({ ...formData, valor: value });
              }}
              placeholder="0,00"
              required
            />
          </label>

          <label className="ns-field">
            <span>Descrição do Serviço *</span>
            <input
              className="ns-input"
              value={formData.descricaoServico}
              onChange={(e) =>
                setFormData({ ...formData, descricaoServico: e.target.value })
              }
              required
            />
          </label>

          <label className="ns-field">
            <span>Data de Vencimento</span>
            <input
              className="ns-input"
              type="date"
              value={formData.dataVencimento}
              onChange={(e) =>
                setFormData({ ...formData, dataVencimento: e.target.value })
              }
            />
          </label>

          <label className="ns-field">
            <span>Observações</span>
            <textarea
              className="ns-textarea"
              value={formData.observacoes}
              onChange={(e) =>
                setFormData({ ...formData, observacoes: e.target.value })
              }
              rows={3}
            />
          </label>

          <div style={{ marginTop: '16px', padding: '16px', backgroundColor: 'var(--page-surface-hover)', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}>Assinatura do Nutricionista</h4>
            <p style={{ marginBottom: '12px', fontSize: '12px', color: 'var(--page-text-muted)' }}>
              Se não preenchido, será usado o nome do emitente (nutricionista) automaticamente.
            </p>
            
            <label className="ns-field">
              <span>Nome do Nutricionista</span>
              <input
                className="ns-input"
                value={formData.assinaturaNome}
                onChange={(e) =>
                  setFormData({ ...formData, assinaturaNome: e.target.value })
                }
                placeholder={formData.emitente.nome || "Digite o nome completo para assinatura"}
              />
              {!formData.assinaturaNome && formData.emitente.nome && (
                <p style={{ marginTop: '4px', fontSize: '11px', color: 'var(--page-text-muted)', fontStyle: 'italic' }}>
                  Será usado: {formData.emitente.nome}
                </p>
              )}
            </label>

            <label className="ns-field">
              <span>Data da Assinatura</span>
              <input
                className="ns-input"
                type="date"
                value={formData.assinaturaData}
                onChange={(e) =>
                  setFormData({ ...formData, assinaturaData: e.target.value })
                }
              />
              {!formData.assinaturaData && (
                <p style={{ marginTop: '4px', fontSize: '11px', color: 'var(--page-text-muted)', fontStyle: 'italic' }}>
                  Será usada a data de hoje se não informada
                </p>
              )}
            </label>
          </div>

          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: 'var(--page-surface-hover)', borderRadius: '8px' }}>
            <p style={{ margin: 0, fontSize: '14px' }}>
              <strong>Dados do Emitente:</strong>
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
              {formData.emitente.nome || 'Não configurado'}
            </p>
            <Button
              variant="ghost"
              onClick={() => setShowSettings(true)}
              style={{ marginTop: '8px' }}
            >
              Editar Dados do Emitente
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

